import { FC, MouseEvent, useCallback } from "react";
import { useNavigate } from "react-router";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { IconButton, Tooltip, alpha, useTheme } from "@mui/material";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { useFetchNodeDiagramPublicity } from "@/components/Procedures/NodeDiagram/API/NodeDiagramQueries";

import { Procedure } from "@/model/Procedure/Procedure";

interface NodeDiagramButtonProps {
	item: Procedure;
}

export const NodeDiagramButton: FC<NodeDiagramButtonProps> = ({ item }) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const { data: nodeDiagramPublicity } = useFetchNodeDiagramPublicity();

	const go = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			e.preventDefault();
			navigate(`/procedure/${item.Id}/nodeDiagram`);
		},
		[navigate, item.Id],
	);

	// Visibility rules
	const isGlobalDraft = Boolean(nodeDiagramPublicity?.Draft);
	const isItemPublic = Boolean(item.NodeDiagramPublicity);
	const showAll = item.NodeDiagramVisibility === "ShowAll";
	if (!isGlobalDraft && !isItemPublic && !showAll) return null;

	const commonButton = (
		<Tooltip
			title={isGlobalDraft ? "Node diagram (draft)" : "Open node diagram"}
			enterDelay={500}
			arrow
		>
			<IconButton
				aria-label="Open node diagram"
				onClick={go}
				size="small"
				sx={{
					borderRadius: 2,
					border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
					backgroundColor:
						theme.palette.mode === "light"
							? alpha(theme.palette.info.main, 0.05)
							: alpha(theme.palette.info.light, 0.08),
					transition: "background-color .2s, border-color .2s",
					"&:hover": {
						backgroundColor:
							theme.palette.mode === "light"
								? alpha(theme.palette.info.main, 0.12)
								: alpha(theme.palette.info.light, 0.2),
						borderColor: theme.palette.info.main,
					},
				}}
			>
				<AccountTreeOutlinedIcon
					fontSize="small"
					sx={{ color: theme.palette.info.main }}
				/>
			</IconButton>
		</Tooltip>
	);

	return isGlobalDraft ? (
		<AdminProtected>{commonButton}</AdminProtected>
	) : (
		commonButton
	);
};
